import { useMemo, useEffect, useRef, useState } from 'react';
import { useDeepCompareEffect } from 'ahooks';
import { getRainbowApi, getTrendsList, getTrendsInfo } from '../../services/trendingames';
import { IDownloadImageType, captureNode } from '@intelligence/utils/captureUtils';
import { getImgList } from './_util';
import { buriedPointGame } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '../../utils/buriedPointCfg';

import type { ISimpleDownloadFormModel } from '@intelligence/components/SimpleDownloadSelect';

export function useDownloadReportEffect(props: { title: string }) {
  const { title } = props;
  const node = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = (value: ISimpleDownloadFormModel) => {
    // 这里需要提前更新loading为true，避免setTimeout延时期间，下载弹窗已关闭了，但是下载按钮还没进入loading状态的空白阶段
    setDownloading(true);
    // 这里使用setTimeout 延时300ms，等页面其他UI刷新完后，再截屏，避免 html2canvas截图时占用性能 导致 页面其他UI卡顿的问题
    setTimeout(async () => {
      if (!node.current) {
        setDownloading(false);
        return;
      }
      try {
        const fileFormat = value?.fileFormat || 'png';
        await captureNode({
          type: fileFormat as IDownloadImageType,
          node: node.current,
          fileName: `Trending Games-${title}`,
        });
      } finally {
        setDownloading(false);
      }
    }, 300);
  };

  return {
    node,
    downloading,
    onDownload: handleDownload,
  };
}

export function useImgsLoadingStateEffect(imgList: string[]) {
  // 单张图片的加载状态
  const [imgsLoadings, setImgsLoadings] = useState<boolean[]>([]);

  // 整张图片的加载状态，即所有图片都加载完成，整张图才算加载完成
  const imgsLoading = useMemo<boolean>(() => imgsLoadings?.some((item) => item), [imgsLoadings]);

  const updateImgLoadingState = (index: number) => {
    const imgsLoadingsTmp = [
      ...imgsLoadings.slice(0, index),
      false,
      ...imgsLoadings.slice(index + 1),
    ];
    setImgsLoadings(imgsLoadingsTmp);
  };

  const handleImgLoad = (index: number) => {
    updateImgLoadingState(index);
  };

  const handleImgError = (index: number) => {
    updateImgLoadingState(index);
  };

  useDeepCompareEffect(() => {
    if (!Array.isArray(imgList)) return;
    const imgsLoadingsTmp = imgList.map(() => true);
    setImgsLoadings(imgsLoadingsTmp);
  }, [imgList]);

  return {
    imgsLoading,
    onImgLoad: handleImgLoad,
    onImgError: handleImgError,
  };
}

export function useCurrentReportEffect(props: {
  games: any[];
  dateVersions: {
    [key: string]: any;
  };
}) {
  const { games, dateVersions } = props;

  const [game, setGame] = useState<any>(null);
  const [dateVersion, setDateVersion] = useState<string>('');
  const title = useMemo<string>(() => {
    if (!game?.name) return '';
    return [game?.name, dateVersion].filter((item: string) => item?.trim()?.length).join('-');
  }, [game?.name, dateVersion]);

  const handleGameSelect = (payload: any) => {
    setGame(payload || null);
  };

  const handleDateVersionSelect = (payload: string) => {
    setDateVersion(payload || '');
  };

  useDeepCompareEffect(() => {
    const gameTmp = games?.[0] || null;
    const dateVersionTmp = dateVersions?.[0] || '';
    setGame(gameTmp);
    setDateVersion(dateVersionTmp);
  }, [games, dateVersions]);

  return {
    // game,
    // dateVersion,
    title,
    onGameSelect: handleGameSelect,
    onDateVersionSelect: handleDateVersionSelect,
  };
}

export default (props: any) => {
  const Urluid = props?.match?.params?.uid;
  const Urlver = props?.match?.params?.ver;

  const [selectedIdx, setSelectedIdx] = useState('');
  const [selectedUidAndId, setSelectedUidAndId] = useState('');
  const [isShow, setIsShow] = useState(true);
  const [headerList, setHeaderList] = useState([]);
  const [timeList, setTimeList] = useState({});
  const [imgInfo, setImgInfo] = useState({});
  const [selectedDate, setSelectedDate] = useState('');
  const [imgList, setImgList] = useState<string[]>([]);
  const [imgLoading, setImgLoading] = useState(true);
  const [headerLoading, setHeaderLoading] = useState(true);

  const segRef = useRef<HTMLDivElement>(null);
  const ScrollRef = useRef<HTMLDivElement>(null);
  let lastScrollTop = 0;
  const pageLogMes = dbiPageLogMes.trendingGames;

  /**
   * @description: 获得游戏列表，根据Url设置
   * @return {*}
   */
  const getHeaderList = async () => {
    setHeaderLoading(true);
    try {
      // const res = await getRainbowApi({
      //   "key": "trends_report_thumbnail_list", "group": "trends_report.config"
      // })
      // let tmpHeaderList = JSON.parse(res.rows[0].value)

      // 替换管理端接口
      const res = await getTrendsList({});

      const tmpHeaderList = res || [];
      if (tmpHeaderList.length > 0) {
        const findIdx = tmpHeaderList.find((obj: { unified_id: any }) => obj.unified_id === Urluid);
        if (findIdx) {
          setSelectedIdx(findIdx?.unified_id);
          setSelectedUidAndId(findIdx?.unified_id + findIdx?.id);
          setSelectedDate(Urlver);
          await getTimeList(findIdx?.unified_id, Urlver, findIdx?.type, findIdx?.id);
        } else {
          setSelectedIdx(tmpHeaderList[0]?.unified_id);
          setSelectedUidAndId(tmpHeaderList[0]?.unified_id + tmpHeaderList[0]?.id);
          await getTimeList(
            tmpHeaderList[0]?.unified_id,
            null,
            tmpHeaderList[0]?.type,
            tmpHeaderList[0]?.id,
          );
        }
        setHeaderList(tmpHeaderList);
      }
      setHeaderLoading(false);
    } catch (err) {}
  };

  /**
   * @description: 更具uid获得该游戏的所有推送，ver是设置推送日期
   * @param {string} uid
   * @param {*} ver
   * @return {*}
   */
  const getTimeList = async (uid: string, ver, type: string, listId = '') => {
    const idValue = uid + listId;
    setImgLoading(true);
    if (idValue !== selectedUidAndId) {
      // if (uid !== selectedIdx) {
      try {
        // if (!(uid in timeList)) {
        // const res = await getRainbowApi({
        //   "key": `trends_report_poster_pc_${uid}`,
        //   "group": "trends_report.poster.pc"
        // })

        // let tmpTimeList = JSON.parse(res.rows[0].value)
        const res = await getTrendsInfo({
          key: `pc_${uid}`,
        });

        const tmpTimeList = res?.sort || [];
        const info = res?.info || {};
        setImgInfo(info);

        if (tmpTimeList.length <= 0) {
        } else {
          timeList[uid] = tmpTimeList;
          setTimeList(timeList);
        }
        // }
        setSelectedIdx(uid);
        setSelectedUidAndId(uid + listId);
        if (ver) {
          setSelectedDate(ver);
        } else {
          setSelectedDate('');
        }
      } catch (err) {}
    }
    setImgLoading(false);
    buriedPointGame(pageLogMes, uid);
  };

  /**
   * @description: 推送滚动时 Header隐藏
   * @return {*}
   */
  const handleScroll = () => {
    if (ScrollRef.current!.scrollTop > lastScrollTop) {
      setIsShow(false);
      lastScrollTop = ScrollRef.current!.scrollTop;
    } else if (ScrollRef.current!.scrollTop < lastScrollTop) {
      setIsShow(true);
      lastScrollTop = ScrollRef.current!.scrollTop;
    }
  };

  /**
   * @description: 从子组件获得新日期
   * @param {*} val
   * @return {*}
   */
  const getSelectedDate = (val) => {
    setSelectedDate(val);
  };

  useEffect(() => {
    getHeaderList();
    ScrollRef.current?.addEventListener('scroll', handleScroll);

    return () => ScrollRef.current?.removeEventListener('scroll', handleScroll);
  }, []);

  /**
   * @description: 更具selectedDateh和selectedIdx改变推送的图片列表
   * @return {*}
   */
  useEffect(() => {
    ScrollRef.current!.scrollTop = 0;
    setImgList(getImgList(selectedIdx, timeList[selectedIdx], selectedDate, imgInfo));
    // setImgLoading(false)
  }, [selectedDate, selectedIdx, imgInfo]);

  return {
    selectedIdx,
    selectedUidAndId,
    isShow,
    headerList,
    timeList,
    selectedDate,
    imgList,
    ScrollRef,
    segRef,
    getTimeList,
    getSelectedDate,
    imgLoading,
    headerLoading,
  };
};
